import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../services/api';
import i18n from '../i18n';
import { I18N_UI_LANGUAGE_OPTIONS, normalizeToSupportedUiLanguage } from '../constants/uiLanguages';
import { syncUiLanguageToMemberProfile } from '../utils/syncUiLanguageToMemberProfile';
import {
  buildFirstLoginNavigateState,
  getPostFirstLoginDestination,
} from '../utils/firstLoginFlow';

const FirstLoginChangePasswordMobile = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const returnFallback = location.state?.returnFallback || '/dashboard';
  const { t } = useTranslation('common');
  const { token, user, loading, loginWithToken, logout, setMobilePin } = useAuth();
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [uiLanguage, setUiLanguage] = useState(() => normalizeToSupportedUiLanguage(i18n.language));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setUiLanguage(normalizeToSupportedUiLanguage(i18n.language));
  }, [i18n.language]);

  useEffect(() => {
    if (!loading && !token) navigate('/mobile/login', { replace: true });
  }, [loading, token, navigate]);

  useEffect(() => {
    if (!loading && token && user && !user.must_change_password) {
      navigate(getPostFirstLoginDestination(user, { fallback: returnFallback }), {
        replace: true,
        state: buildFirstLoginNavigateState(user, { fallback: returnFallback }),
      });
    }
  }, [loading, token, user, navigate, returnFallback]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword.length < 8) {
      setError('Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (newPassword !== confirm) {
      setError('La confirmation ne correspond pas.');
      return;
    }
    if (String(pin || '').trim().length < 4) {
      setError('Le PIN doit contenir au moins 4 chiffres.');
      return;
    }
    if (pin !== pinConfirm) {
      setError('La confirmation du PIN ne correspond pas.');
      return;
    }
    setSubmitting(true);
    try {
      const projectId = localStorage.getItem('project_id');
      const res = await authAPI.changePassword(oldPassword, newPassword, projectId);
      const nextUser = res.data?.user;
      loginWithToken(res.data?.access_token, nextUser);
      await syncUiLanguageToMemberProfile(nextUser, uiLanguage);
      const ok = setMobilePin(pin, nextUser?.id);
      if (!ok) {
        setError('Mot de passe mis à jour, mais impossible d’enregistrer le PIN sur cet appareil.');
        return;
      }
      navigate(getPostFirstLoginDestination(nextUser, { fallback: returnFallback }), {
        replace: true,
        state: buildFirstLoginNavigateState(nextUser, { fallback: returnFallback }),
      });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Impossible de mettre à jour le mot de passe.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !token) {
    return (
      <div className="min-h-screen bg-[#f4f6fb] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-entrea-royal" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f6fb]">
      <div className="mx-auto w-full max-w-md min-h-screen flex flex-col px-5 pt-10 pb-[calc(env(safe-area-inset-bottom)+16px)]">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-extrabold text-slate-900">Sécuriser votre compte</h1>
          <p className="text-sm text-slate-500 mt-1">Choisissez votre mot de passe personnel</p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white shadow-[0_10px_28px_rgba(15,51,80,0.10)] p-5">
          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('firstLogin.defaultLanguage')}
              </label>
              <select
                value={uiLanguage}
                onChange={(e) => setUiLanguage(normalizeToSupportedUiLanguage(e.target.value))}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base bg-white focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
              >
                {I18N_UI_LANGUAGE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="mt-1.5 text-xs text-slate-500">{t('firstLogin.defaultLanguageHint')}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Mot de passe actuel</label>
              <input
                type="password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Nouveau mot de passe</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirmer</label>
              <input
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                required
              />
            </div>
            <div className="pt-1 border-t border-slate-100">
              <label className="block text-sm font-medium text-slate-700 mb-2">Créer un PIN (appareil)</label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={pin}
                onChange={(e) => setPin(String(e.target.value || '').replace(/\D/g, ''))}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                placeholder="PIN (4 chiffres minimum)"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Confirmer le PIN</label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                value={pinConfirm}
                onChange={(e) => setPinConfirm(String(e.target.value || '').replace(/\D/g, ''))}
                className="w-full min-h-[52px] rounded-xl border border-slate-300 px-4 text-base focus:outline-none focus:ring-2 focus:ring-entrea-royal/30"
                placeholder="Confirmer le PIN"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full min-h-[52px] rounded-xl bg-entrea-royal text-white font-semibold text-base shadow-sm active:scale-[0.99] disabled:opacity-60"
            >
              {submitting ? 'Enregistrement…' : 'Enregistrer et continuer'}
            </button>
          </form>
          <button
            type="button"
            onClick={() => logout()}
            className="mt-4 w-full text-sm text-slate-500 hover:text-slate-700"
          >
            Se déconnecter
          </button>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500">
          <p>
            Retour version web:{' '}
            <Link to="/first-login-password" className="text-entrea-royal underline underline-offset-2">ouvrir</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default FirstLoginChangePasswordMobile;
